import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type ReactElement,
  type SetStateAction,
} from 'react';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  tablePanelTabSx,
  tablePanelTabsSx,
} from '@/features/shared/components/tableSystem/tablePanelTabs';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  testRunColumnOptions as tabbedExecutionColumnOptions,
  testRunDefaultColumnVisibilityModel as tabbedExecutionDefaultColumnVisibilityModel,
  testRunDefaultSortDirection as tabbedExecutionDefaultSortDirection,
  testRunDefaultSortKey as tabbedExecutionDefaultSortKey,
  testRunSortOptions as tabbedExecutionSortOptions,
  testRunStatusOptions as tabbedExecutionStatusOptions,
  type TestRunSortDirection,
  type TestRunSortKey,
} from '@/features/tests/pages/testRunsTableDefinition';
import {
  type TestSortDirection,
  type TestSortKey,
  testsTableDefinition,
} from '@/features/tests/pages/testsTableDefinition';
import { getFilterLabel } from '@/shared/copy/filters';
import { Stack } from '@/ui';
import { Panel } from '@/ui/composites/Panel';
import { Box, Tab, Tabs, type GridColumnVisibilityModel } from '@/ui/primitives';
import { TestRunsTable } from '../components/TestRunsTable';
import { TestsTable } from '../components/TestsTable';
import type { TestStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestRunDto, TestRunStatus, TestListItemDto } from '@flowqa/shared';
import type { Dayjs } from 'dayjs';

type TabbedTestsPanelTab = 'tests' | 'executions';

const tableTabPanelSx = {
  minWidth: 0,
  position: 'relative',
  width: '100%',
  zIndex: 1,
} as const;

export type TestsPageTabbedPanelProps = {
  panelActiveTab: TabbedTestsPanelTab;
  setPanelActiveTab: Dispatch<SetStateAction<TabbedTestsPanelTab>>;
  panelDateQuickFilter: '7d' | '14d' | '30d' | 'latest';
  setPanelDateQuickFilter: Dispatch<SetStateAction<'7d' | '14d' | '30d' | 'latest'>>;
  panelFromDate: Dayjs | null;
  setPanelFromDate: Dispatch<SetStateAction<Dayjs | null>>;
  panelToDate: Dayjs | null;
  setPanelToDate: Dispatch<SetStateAction<Dayjs | null>>;
  panelColumnVisibilityModel: GridColumnVisibilityModel;
  setPanelColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  panelSortKey: TestSortKey;
  setPanelSortKey: Dispatch<SetStateAction<TestSortKey>>;
  panelSortDirection: TestSortDirection;
  setPanelSortDirection: Dispatch<SetStateAction<TestSortDirection>>;
  panelFilterStatuses: TestRunStatus[];
  setPanelFilterStatuses: Dispatch<SetStateAction<TestRunStatus[]>>;
  panelFilterTests: string[];
  setPanelFilterTests: Dispatch<SetStateAction<string[]>>;
  panelFilterSuites: string[];
  setPanelFilterSuites: Dispatch<SetStateAction<string[]>>;
  panelFilterTags: string[];
  setPanelFilterTags: Dispatch<SetStateAction<string[]>>;
  panelExecutionColumnVisibilityModel: GridColumnVisibilityModel;
  setPanelExecutionColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  panelExecutionSortKey: TestRunSortKey;
  setPanelExecutionSortKey: Dispatch<SetStateAction<TestRunSortKey>>;
  panelExecutionSortDirection: TestRunSortDirection;
  setPanelExecutionSortDirection: Dispatch<SetStateAction<TestRunSortDirection>>;
  panelAvailableTags: string[];
  suiteOptions: Array<{ value: string; label: string }>;
  testOptions: Array<{ value: string; label: string }>;
  panelDateWindow: { startDay: string | null; endDay: string | null; sparklineRangeDays: number };
  panelStatusFilterKey: string;
  panelTestFilterKey: string;
  panelSuiteFilterKey: string;
  panelTagFilterKey: string;
  panelNoRowsText: string;
  panelExecutionsNoRowsText: string;
  debouncedPanelSearch: string;
  panelTestsMeta: TableQueryMeta<TestListItemDto> | null;
  setPanelTestsMeta: Dispatch<SetStateAction<TableQueryMeta<TestListItemDto> | null>>;
  panelTestRunsMeta: TableQueryMeta<TestRunDto> | null;
  setPanelTestRunsMeta: Dispatch<SetStateAction<TableQueryMeta<TestRunDto> | null>>;
  panelTestRunDateBounds: { from: Dayjs | null; to: Dayjs | null };
  showPanelDeltas: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  handleSearchChange: (value: string) => void;
  handleRangeChange: (value: '7d' | '14d' | '30d' | 'latest') => void;
  handleSelectedStartDayChange: (value: string | null) => void;
  handleSelectedEndDayChange: (value: string | null) => void;
  handleStatusesChange: (value: TestStatusKey[]) => void;
  handleTagsChange: (value: string[]) => void;
  handleDebouncedPanelSearchChange: (value: string) => void;
  panelQueryFn: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  panelExecutionQueryFn: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  tagOptionsOverride: TagOption[];
};

export const TestsPageTabbedPanel = ({
  panelActiveTab,
  setPanelActiveTab,
  panelDateQuickFilter,
  setPanelDateQuickFilter,
  panelFromDate,
  setPanelFromDate,
  panelToDate,
  setPanelToDate,
  panelColumnVisibilityModel,
  setPanelColumnVisibilityModel,
  panelSortKey,
  setPanelSortKey: _setPanelSortKey,
  panelSortDirection,
  setPanelSortDirection: _setPanelSortDirection,
  panelFilterStatuses,
  setPanelFilterStatuses,
  panelFilterTests,
  setPanelFilterTests,
  panelFilterSuites,
  setPanelFilterSuites,
  panelFilterTags,
  setPanelFilterTags,
  panelExecutionColumnVisibilityModel,
  setPanelExecutionColumnVisibilityModel,
  panelExecutionSortKey,
  setPanelExecutionSortKey,
  panelExecutionSortDirection,
  setPanelExecutionSortDirection,
  panelAvailableTags,
  suiteOptions,
  testOptions,
  panelDateWindow,
  panelStatusFilterKey,
  panelTestFilterKey,
  panelSuiteFilterKey,
  panelTagFilterKey,
  panelNoRowsText,
  panelExecutionsNoRowsText,
  debouncedPanelSearch,
  panelTestsMeta,
  setPanelTestsMeta,
  panelTestRunsMeta,
  setPanelTestRunsMeta,
  panelTestRunDateBounds,
  showPanelDeltas,
  selectedOrganisationId,
  selectedProjectId,
  handleSearchChange,
  handleRangeChange,
  handleSelectedStartDayChange,
  handleSelectedEndDayChange,
  handleStatusesChange,
  handleTagsChange,
  handleDebouncedPanelSearchChange,
  panelQueryFn,
  panelExecutionQueryFn,
  tagOptionsOverride,
}: TestsPageTabbedPanelProps): ReactElement => {
  const [tableSortKey, setTableSortKey] = useState<TestSortKey>(panelSortKey);
  const [tableSortDirection, setTableSortDirection] =
    useState<TestSortDirection>(panelSortDirection);
  const [tableTestsMeta, setTableTestsMeta] = useState<TableQueryMeta<TestListItemDto> | null>(
    panelTestsMeta,
  );
  const hasLocalTableSort =
    tableSortKey !== panelSortKey || tableSortDirection !== panelSortDirection;

  useEffect(() => {
    setTableSortKey(panelSortKey);
  }, [panelSortKey]);

  useEffect(() => {
    setTableSortDirection(panelSortDirection);
  }, [panelSortDirection]);

  useEffect(() => {
    setTableTestsMeta(panelTestsMeta);
  }, [panelTestsMeta]);

  const handleTestsQueryMeta = useCallback(
    (meta: TableQueryMeta<TestListItemDto> | null) => {
      setTableTestsMeta(meta);
      if (!hasLocalTableSort) {
        setPanelTestsMeta(meta);
      }
    },
    [hasLocalTableSort, setPanelTestsMeta],
  );

  const tableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> =>
      await panelQueryFn({
        ...input,
        sorts: [{ field: tableSortKey, direction: tableSortDirection }],
      }),
    [panelQueryFn, tableSortDirection, tableSortKey],
  );

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      sx={tablePanelSurfaceSx}
      headerSlot={
        <Box
          sx={{
            backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
            backgroundImage: 'none',
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
              backgroundImage: 'none',
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={panelActiveTab}
                onChange={(_event, next) => {
                  setPanelActiveTab(next as TabbedTestsPanelTab);
                }}
                aria-label="Tests panel tabs"
                sx={tablePanelTabsSx}
              >
                <Tab label="Tests" value="tests" sx={tablePanelTabSx} />
                <Tab label="Runs" value="executions" sx={tablePanelTabSx} />
              </Tabs>
            </Box>
            {panelActiveTab === 'tests' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<TestSortKey, TestRunStatus>
                  title={undefined}
                  searchAriaLabel={testsTableDefinition.searchAriaLabel}
                  searchValue={debouncedPanelSearch}
                  onDebouncedSearchChange={(value) => {
                    handleDebouncedPanelSearchChange(value);
                    handleSearchChange(value);
                  }}
                  columnVisibilityModel={panelColumnVisibilityModel}
                  defaultColumnVisibilityModel={testsTableDefinition.defaultColumnVisibilityModel}
                  columnOptions={testsTableDefinition.columnOptions}
                  onColumnVisibilityModelChange={setPanelColumnVisibilityModel}
                  sortKey={tableSortKey}
                  sortDirection={tableSortDirection}
                  defaultSortKey={testsTableDefinition.defaultSortKey}
                  defaultSortDirection={testsTableDefinition.defaultSortDirection}
                  sortOptions={testsTableDefinition.sortOptions}
                  quickSortOptions={testsTableDefinition.quickSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setTableSortKey(nextSortKey);
                    setTableSortDirection(nextSortDirection);
                  }}
                  filterStatuses={panelFilterStatuses}
                  statusOptions={testsTableDefinition.statusOptions}
                  onFilterStatusesChange={(next) => {
                    setPanelFilterStatuses(next);
                    handleStatusesChange(next as TestStatusKey[]);
                  }}
                  filterEntities={panelFilterTests}
                  onFilterEntitiesChange={setPanelFilterTests}
                  entityOptions={testOptions}
                  entityLabel="Test"
                  filterSuites={panelFilterSuites}
                  onFilterSuitesChange={setPanelFilterSuites}
                  suiteOptions={suiteOptions}
                  filterTags={panelFilterTags}
                  onFilterTagsChange={(next) => {
                    setPanelFilterTags(next);
                    handleTagsChange(next);
                  }}
                  dateQuickFilter={panelDateQuickFilter}
                  onDateQuickFilterChange={(next) => {
                    setPanelDateQuickFilter(next);
                    setPanelFromDate(null);
                    setPanelToDate(null);
                    handleRangeChange(next);
                    handleSelectedStartDayChange(null);
                    handleSelectedEndDayChange(null);
                  }}
                  fromDate={panelFromDate}
                  toDate={panelToDate}
                  hideDateRangeLabel
                  showControlsWithoutSummary
                  onFromDateChange={(next) => {
                    setPanelFromDate(next);
                    handleSelectedStartDayChange(next ? next.format('YYYY-MM-DD') : null);
                  }}
                  onToDateChange={(next) => {
                    setPanelToDate(next);
                    handleSelectedEndDayChange(next ? next.format('YYYY-MM-DD') : null);
                  }}
                  availableTags={panelAvailableTags}
                  summaryCount={tableTestsMeta?.total ?? null}
                  summarySingularLabel="test"
                  summaryPluralLabel="tests"
                />
              </Box>
            ) : null}
            {panelActiveTab === 'executions' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<TestRunSortKey, TestRunStatus>
                  title={undefined}
                  searchAriaLabel="Search test runs"
                  searchValue={debouncedPanelSearch}
                  onDebouncedSearchChange={(value) => {
                    handleDebouncedPanelSearchChange(value);
                    handleSearchChange(value);
                  }}
                  columnVisibilityModel={panelExecutionColumnVisibilityModel}
                  defaultColumnVisibilityModel={tabbedExecutionDefaultColumnVisibilityModel}
                  columnOptions={tabbedExecutionColumnOptions}
                  onColumnVisibilityModelChange={setPanelExecutionColumnVisibilityModel}
                  sortKey={panelExecutionSortKey}
                  sortDirection={panelExecutionSortDirection}
                  defaultSortKey={tabbedExecutionDefaultSortKey}
                  defaultSortDirection={tabbedExecutionDefaultSortDirection}
                  sortOptions={tabbedExecutionSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setPanelExecutionSortKey(nextSortKey);
                    setPanelExecutionSortDirection(nextSortDirection);
                  }}
                  filterStatuses={panelFilterStatuses}
                  statusOptions={tabbedExecutionStatusOptions}
                  onFilterStatusesChange={(next) => {
                    setPanelFilterStatuses(next);
                    handleStatusesChange(next as TestStatusKey[]);
                  }}
                  filterEntities={panelFilterTests}
                  onFilterEntitiesChange={setPanelFilterTests}
                  entityOptions={testOptions}
                  entityLabel="Test"
                  filterSuites={panelFilterSuites}
                  onFilterSuitesChange={setPanelFilterSuites}
                  suiteOptions={suiteOptions}
                  filterTags={panelFilterTags}
                  onFilterTagsChange={(next) => {
                    setPanelFilterTags(next);
                    handleTagsChange(next);
                  }}
                  dateQuickFilter={panelDateQuickFilter}
                  onDateQuickFilterChange={(next) => {
                    setPanelDateQuickFilter(next);
                    setPanelFromDate(null);
                    setPanelToDate(null);
                    handleRangeChange(next);
                    handleSelectedStartDayChange(null);
                    handleSelectedEndDayChange(null);
                  }}
                  fromDate={panelFromDate}
                  toDate={panelToDate}
                  onFromDateChange={(next) => {
                    setPanelFromDate(next);
                    handleSelectedStartDayChange(next ? next.format('YYYY-MM-DD') : null);
                  }}
                  onToDateChange={(next) => {
                    setPanelToDate(next);
                    handleSelectedEndDayChange(next ? next.format('YYYY-MM-DD') : null);
                  }}
                  availableTags={panelAvailableTags}
                  includeLatestQuickFilter
                  statusFilterLabel={getFilterLabel('status')}
                  summaryCount={panelTestRunsMeta?.total ?? null}
                  summarySingularLabel="test run"
                  summaryPluralLabel="test runs"
                  previewFromDate={
                    panelDateQuickFilter === 'latest' ? panelTestRunDateBounds.from : null
                  }
                  previewToDate={
                    panelDateQuickFilter === 'latest' ? panelTestRunDateBounds.to : null
                  }
                  hideDateRangeLabel
                  showControlsWithoutSummary
                />
              </Box>
            ) : null}
          </Stack>
        </Box>
      }
    >
      <Box sx={{ minWidth: 0, position: 'relative', width: '100%' }}>
        <Box
          role="tabpanel"
          aria-hidden={panelActiveTab === 'tests' ? undefined : true}
          sx={tableTabPanelSx}
        >
          {panelActiveTab === 'tests' ? (
            <TestsTable
              persistKey={null}
              showPaginationSkeletonWhenRowsVisible
              queryKey={[
                'tests-table',
                panelDateQuickFilter,
                selectedOrganisationId ?? null,
                selectedProjectId ?? null,
                showPanelDeltas ? 'deltas-on' : 'deltas-off',
                tableSortKey,
                tableSortDirection,
                panelStatusFilterKey,
                panelTestFilterKey,
                panelSuiteFilterKey,
                panelTagFilterKey,
                panelDateWindow.startDay,
                panelDateWindow.endDay,
              ]}
              queryFn={tableQueryFn}
              pageSizeOptions={[5, 10, 25, 50]}
              initialPageSize={5}
              preservePreviousData
              initialSortModel={[
                {
                  field: tableSortKey,
                  sort: tableSortDirection,
                },
              ]}
              showDeltas={showPanelDeltas}
              sparklineRangeDays={panelDateWindow.sparklineRangeDays}
              metricsStartDay={panelDateWindow.startDay ?? undefined}
              metricsEndDay={panelDateWindow.endDay ?? undefined}
              noRowsText={panelNoRowsText}
              showToolbar={false}
              disableColumnSorting
              disableColumnMenu
              search={debouncedPanelSearch}
              onQueryMeta={handleTestsQueryMeta}
              tagOptionsOverride={tagOptionsOverride}
              columnVisibilityModel={panelColumnVisibilityModel}
              onColumnVisibilityModelChange={(nextModel) => {
                setPanelColumnVisibilityModel({
                  ...nextModel,
                  name: true,
                });
              }}
            />
          ) : null}
        </Box>
        <Box
          role="tabpanel"
          aria-hidden={panelActiveTab === 'executions' ? undefined : true}
          sx={tableTabPanelSx}
        >
          {panelActiveTab === 'executions' ? (
            <TestRunsTable
              persistKey={null}
              showPaginationSkeletonWhenRowsVisible
              queryKey={[
                'test-runs-table',
                panelDateQuickFilter,
                selectedOrganisationId ?? null,
                selectedProjectId ?? null,
                panelExecutionSortKey,
                panelExecutionSortDirection,
                panelStatusFilterKey,
                panelTestFilterKey,
                panelSuiteFilterKey,
                panelTagFilterKey,
                debouncedPanelSearch,
                panelDateWindow.startDay,
                panelDateWindow.endDay,
              ]}
              queryFn={panelExecutionQueryFn}
              pageSizeOptions={[5, 10, 25, 50]}
              initialPageSize={5}
              noRowsText={panelExecutionsNoRowsText}
              showToolbar={false}
              disableColumnSorting
              disableColumnMenu
              search={debouncedPanelSearch}
              onQueryMeta={setPanelTestRunsMeta}
              tagOptionsOverride={tagOptionsOverride}
              columnVisibilityModel={panelExecutionColumnVisibilityModel}
              onColumnVisibilityModelChange={(nextModel) => {
                setPanelExecutionColumnVisibilityModel({
                  ...nextModel,
                  name: true,
                });
              }}
            />
          ) : null}
        </Box>
      </Box>
    </Panel>
  );
};
